import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { 
  Button as KendoButton, 
  Toolbar, 
  ToolbarItem
} from '@progress/kendo-react-buttons';
import { classNames } from '@progress/kendo-react-common';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnProps,
  GridFooterCellProps,
  GridHeaderSelectionChangeEvent,
  GridNoRecords,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
  GridSortChangeEvent
} from '@progress/kendo-react-grid';
import { 
  GRID_COL_INDEX_ATTRIBUTE
} from '@progress/kendo-react-grid/dist/npm/constants';
import { CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import * as _ from 'lodash';
import React from 'react';
import { Position, ProgressBar, PuzzleTooltip } from '../..';
import PuzzleCheckbox from '../UI/checkbox/PuzzleCheckbox';
import { MultipleResponseByUploadGroup } from '../../dispatcher/ajax/Ajax';
import PuzzleButton from '../UI/button/PuzzleButton';
import Container from '../layout/container/Container';
import DragAndDropArea from './DragAndDropArea';
import { Component } from '../../dispatcher/modules';
import { error } from '../../utility/Util';
import Dispatcher from '../../dispatcher/Dispatcher';
import HBox from '../layout/container/h_box/HBox';
import PuzzleInfoBar from '../layout/panel/PuzzleInfoBar';
import {
  COLUMN_DATA,
  INITIAL_RESPONSE_OBJECT,
  SORT,
  SORTABLE_COLUMN,
  UPLOAD_PANEL_PROPS,
  UPLOAD_PANEL_STATE,
  DATA_ITEM,
  COUNTER,
  CUSTOM_RENDERER_RETURN_TYPE, 
  GRID_PROPS_TYPES
} from './Types';
import { ProgressType } from '../../dispatcher/modules/progressbar/Types';

export const ANNOUNCEMENTS_TYPE = {
  UPLOAD: 'upload',
  DELETE: 'delete'
};
const REMOVE_ANNOUNCEMENT_TIMEOUT: number = 1000;
const ACCESSIBILITY_ANNOUNCEMENTS_CLASS: string = 'accessibilityAnnouncements';

const DEFAULT_SELECTION_FIELD: string = 'selected';
const SELECTION_COLUMN: Partial<COLUMN_DATA> = {
  sortable: false,
  header: 'Checkbox_header',
  hidden: false,
  dataIndex: DEFAULT_SELECTION_FIELD,
  width: 50
};

const DELETE_FILE_DATA_INDEX: string = 'deleteFile';
const DELETE_ROW_ADDITIONAL_COLUMN: Partial<COLUMN_DATA> = {
  sortable: false,
  header: 'delete_upload_column',
  hidden: false,
  dataIndex: DELETE_FILE_DATA_INDEX,
  width: 50
};

export const UPLOAD_FILE_STATUS = {
  analysing: 'analysing',
  analyzed: 'analyzed',
  uploaded: 'uploaded',
  failed: 'failed',
  success: 'success'
};

/**
 * Create <td> attributes
 * @param cellProps
 */
const getTableTdAccessibilityProps = (cellProps: GridCellProps) => ({
// note if grid has navigatable={true} navigation props should be copied too useTableKeyboardNavigation(cellProps.id);
  colSpan: cellProps.colSpan,
  role: 'gridcell',
  'aria-colindex': cellProps.ariaColumnIndex,
  'aria-selected': cellProps.isSelected,
  ...{ [GRID_COL_INDEX_ATTRIBUTE]: cellProps.columnIndex }
});

class UploadPanel extends Component<UPLOAD_PANEL_PROPS, UPLOAD_PANEL_STATE> {
  static defaultProps: Partial<UPLOAD_PANEL_PROPS> = {
    uploadConfig: {},
    isUploadEnabled: true,
    prependSelectionColumn: false,
    appendCloseColumn: false,
    enableExtractArchive: false,
    showDeleteAllFilesButton: true,
    showUploadStatus: false,
    concurrently: false,
    pageSize: 200,
    rowHeight: 32,
    selectionField: DEFAULT_SELECTION_FIELD,
    useAnnouncements: true,
    columns: [],
    showedUploadProgress: true,
    dragAndDropLabels: {},
    uploadPanelLabels: {}
  };
  state: UPLOAD_PANEL_STATE = {
    sort: [],
    skip: 0,
    data: [],
    droppedFiles: [],
    dragging: false,
    extractArchive: {
      value: false,
      enabled: this.props.enableExtractArchive
    },
    columns: [],
    total: 0,
    moduleIsUploading: false,
    progresses: {},
    headerSelection: false
  };
  /**
   * Sortable columns automatically populated based on grid config property. If column configuration contains
   * `sortable` property with value `true` that column will be sortable.
   */
  private sortableColumns: SORTABLE_COLUMN[] = [];
  /**
   * Reference to the `DragAndDropArea` component.
   */
  private dragAndDropReference: DragAndDropArea | null;
  /**
   * It prevents multiple triggering of file analyzing via rerendering of components
   * @private {any[]} previousDataItemAnalyzed
   */
  private previousDataItemAnalyzed: any[] = [];

  constructor(props: UPLOAD_PANEL_PROPS) {
    super(props);
    const { columns = [] } = props;
    this.state = {
      ...this.state,
      extractArchive: {
        value: this.props.enableExtractArchive,
        enabled: _.isUndefined(this.props.showEnableExtractArchive) ? this.props.enableExtractArchive : this.props.showEnableExtractArchive
      },
      columns
    };
  }

  /**
   * Creates list of sortableColumns based on column property `sortable`.
   * @param config
   */
  getSortableColumns = (config: Partial<COLUMN_DATA>[]) => {
    this.sortableColumns = [];
    config.reduce((sortableColumns: SORTABLE_COLUMN[], column: any) => {
      if (column.sortable) {
        sortableColumns.push({
          field: column.dataIndex,
          sortProperty: column.dataIndex
        });
      }
      return sortableColumns;
    }, this.sortableColumns);
  };
  /**
   * Get current sort.
   * @param config
   * @param sort
   */
  getSort = (config: Partial<COLUMN_DATA>[], sort: SORT = {
    property: 'fileName',
    ascending: true
  }): SortDescriptor[] => {
    const gridItem = _.find(config, { dataIndex: sort.property });
    return gridItem ? [{
      dir: this.getSortDirection(sort.ascending),
      field: gridItem.dataIndex
    }] : this.state.sort;
  };
  /**
   * Updates file progresses for current module.
   * @param progresses
   */
  updateFileUploadProgresses = (progresses: {
    [moduleName: string]: {
      [key: string]: number
    }
  } = {}): void => {
    this.setState({
      progresses: progresses[this.moduleId] || {}
    });
  };

  /**
   * Called after prepare upload request is happened. Won't be called when `uploadConfig.isUpload` is `true`.
   * Configures grid. Method is called via dispatcher action.
   * @param uploadStatus_INITIAL
   * @param droppedFiles
   */
  setGridConfig = (uploadStatus_INITIAL: INITIAL_RESPONSE_OBJECT, droppedFiles: File[]): void => {
    let { columns } = this.state;
    const { data = [], config = [], sort = this.props.sort } = uploadStatus_INITIAL || {};
    const filesToUpload: File[] = Array.from(droppedFiles || []);
    columns = columns.length > 0 ? columns : config;
    let progresses = filesToUpload.reduce((acc: { [key: string]: number }, file: File) => {
      acc[file.name] = 0;
      return acc;
    }, {});
    let stateData: Partial<UPLOAD_PANEL_STATE> = {
      progresses
    };
    if (!data.length) {
      let data = filesToUpload.map((file: any) => {
        let additionalData: any = {};
        if (_.isFunction(this.props.changeDataFromServer)) {
          _.assign(additionalData, this.props.changeDataFromServer(file, columns));
        }
        return {
          ...additionalData,
          [this.props.selectionField]: false,
          fileName: file.name,
          status: UPLOAD_FILE_STATUS.analysing
        };
      });
      let { data: initialData } = this.state as UPLOAD_PANEL_STATE;
      let newData: any[] = [...initialData, ...data];
      stateData = {
        ...stateData,
        data: newData,
        total: newData.length
      };
    }
    columns = this.addAdditionalColumns(columns);
    this.getSortableColumns(columns);
    stateData = {
      ...stateData,
      sort: this.getSort(columns, sort),
      columns
    };
    if (Array.isArray(data) && data.length) {
      stateData.data = data;
      stateData.total = data.length;
    }
    const { analyzingAfterUploadConfig = {} } = this.props;
    const { onChangeFileStatuses } = analyzingAfterUploadConfig;
    const fileStatuses = stateData.data;
    if (!_.isEmpty(analyzingAfterUploadConfig) && onChangeFileStatuses && Array.isArray(fileStatuses) && fileStatuses.length) {
      onChangeFileStatuses([...fileStatuses]);
    }
    this.setState(stateData);
  };
  /**
   * Sets grid data. Method is called via dispatcher action.
   * @param uploadStatus
   */
  setGridData = (uploadStatus: MultipleResponseByUploadGroup | any): void => {
    let { droppedFiles, data: filesInAnalysingStatus } = this.state as UPLOAD_PANEL_STATE;
    let { data = [] } = uploadStatus;
    const { uploadConfig } = this.props;

    if (_.isEmpty(data) && _.isObject(uploadStatus) && !_.isEmpty(uploadStatus)) {
      data = [uploadStatus];
    } else if (this.state.extractArchive.value && _.isArray(data) && _.isArray(data[0])) {
      // Block of codes for .zip file and multiple upload files when extractArchive value is true
      const newData = [];
      let matchSubItem: any;
      let decodedFileName: string;
      filesInAnalysingStatus = filesInAnalysingStatus.filter((item) => {
        const fileNameLen = item?.fileName?.length;
        return !(fileNameLen > 0 && item?.fileName?.indexOf('.zip') === fileNameLen - 4);
      });
      data.forEach((item) => {
        item.forEach((subItem) => {
          matchSubItem = _.find(filesInAnalysingStatus, {
            fileName: subItem.filename,
            status: UPLOAD_FILE_STATUS.analysing
          });
          if (!matchSubItem) {
            decodedFileName = decodeURI(subItem.filename);
            matchSubItem = _.find(filesInAnalysingStatus, {
              fileName: decodedFileName,
              status: UPLOAD_FILE_STATUS.analysing
            });
            if (matchSubItem) {
              subItem.filename = decodedFileName;
            }
          }
          if (!matchSubItem) {
            filesInAnalysingStatus.push({
              fileName: subItem.filename,
              status: UPLOAD_FILE_STATUS.analysing
            });
          }
          newData.push({ ...subItem });
        });
      });
      data = [...newData];
    }

    data = data.map((item: any) => {
      if (!item.fileName) {
        const findItemByFileName: any = _.find(filesInAnalysingStatus, {
          path: item.path
        });
        if (findItemByFileName) {
          item.fileName = findItemByFileName?.fileName;
        }
      }
      return item;
    });

    let matchFile: any;
    let additionalData: any;
    for (let i = 0; i < data.length; i++) {
      additionalData = {};
      if (_.isFunction(this.props.changeDataFromServer)) {
        _.assign(additionalData, this.props.changeDataFromServer(data[i], this.state.columns));
      }
      if ([UPLOAD_FILE_STATUS.analyzed, UPLOAD_FILE_STATUS.failed].includes(data[i].status?.toLowerCase())) {
        matchFile = _.find(filesInAnalysingStatus, {
          path: data[i].path || additionalData?.path,
          status: UPLOAD_FILE_STATUS.uploaded
        });
        if (matchFile) {
          _.assign(matchFile, {
            ...additionalData,
            status: data[i].status?.toLowerCase() === UPLOAD_FILE_STATUS.analyzed ? UPLOAD_FILE_STATUS.analyzed : UPLOAD_FILE_STATUS.failed
          });
        }
      } else {
        matchFile = _.find(filesInAnalysingStatus, {
          fileName: data[i].filename,
          status: UPLOAD_FILE_STATUS.analysing
        });
        if (matchFile) {
          _.assign(matchFile, {
            ...additionalData,
            fileName: data[i].filename,
            status: data[i].size > 0 ? UPLOAD_FILE_STATUS.uploaded : UPLOAD_FILE_STATUS.failed
          });
        } else {
          let index: number = _.findIndex(filesInAnalysingStatus, {
            fileName: data[i].filename,
            status: UPLOAD_FILE_STATUS.analysing
          });
          if (index > -1) {
            if (_.isFunction(this.props.changeDataFromServer)) {
              _.assign(additionalData, this.props.changeDataFromServer(filesInAnalysingStatus[index], this.state.columns));
            }
            filesInAnalysingStatus[index] = {
              ...filesInAnalysingStatus[index],
              ...additionalData,
              fileName: data[i].filename,
              status: UPLOAD_FILE_STATUS.failed
            };
          } else {
            const isSimpleRequest = _.get(uploadConfig, 'isUpload', false);

            filesInAnalysingStatus.push({
              ...additionalData,
              fileName: data[i].filename,
              status: isSimpleRequest ? data[i].success ? UPLOAD_FILE_STATUS.success : UPLOAD_FILE_STATUS.failed : UPLOAD_FILE_STATUS.failed
            });
          }
        }
      }
    }

    this.setState({
      moduleIsUploading: false,
      data: filesInAnalysingStatus,
      droppedFiles,
      total: filesInAnalysingStatus.length
    }, () => {
      this.setAnnouncements(ANNOUNCEMENTS_TYPE.UPLOAD, filesInAnalysingStatus);
      if (this.props.onLoadFiles) {
        this.props.onLoadFiles(data);
      }
      const { analyzingAfterUploadConfig = {} } = this.props;
      const { onChangeFileStatuses } = analyzingAfterUploadConfig;
      if (!_.isEmpty(analyzingAfterUploadConfig) && onChangeFileStatuses) {
        onChangeFileStatuses([...this.state.data]);
      }
    });
  };

  /**
   * Method for filtering is column hidden.
   * @param column
   */
  isHidden = ({ hidden }: Partial<COLUMN_DATA>): boolean => !hidden;

  /**
   * Forwards event to default grid selection change handler.
   */
  handleSelectionChange = (selectionHandler: Function | undefined, dataItem: Partial<DATA_ITEM>, syntheticEvent: any): void => {
    if (_.isFunction(selectionHandler)) {
      selectionHandler({ syntheticEvent });
    }
  };

  /**
   * Selection cell render
   * @param cellProps
   */
  selectCellRenderer = (cellProps: GridCellProps): JSX.Element => {
    const { selectionField } = this.props as UPLOAD_PANEL_PROPS;
    const { moduleIsUploading } = this.state as UPLOAD_PANEL_STATE;
    const { dataItem, selectionChange } = cellProps;
    return (
      <PuzzleCheckbox
        label={this.translate('checkboxFileName', {
          fileName: dataItem.fileName
        })}
        disabled={moduleIsUploading}
        checked={!!dataItem[selectionField]}
        onChange={this.handleSelectionChange.bind(this, selectionChange, dataItem)}
      />
    );
  };
  /**
   * Changes dom so screen reader could read changes after files were added or deleted.
   * @param announcementType
   * @param updatedFiles
   */
  setAnnouncements = (announcementType: string, updatedFiles: Partial<DATA_ITEM>[]): void => {
    const { useAnnouncements } = this.props as UPLOAD_PANEL_PROPS;
    if (useAnnouncements && updatedFiles?.length) {
      let el: HTMLElement = this.getDomNode();
      if (el) {
        let alertDiv: HTMLDivElement | null = el.querySelector(`.${ACCESSIBILITY_ANNOUNCEMENTS_CLASS}`);
        if (alertDiv) {
          let text: string = updatedFiles.map((file: Partial<DATA_ITEM>) => file.fileName).join(' ');
          switch (announcementType) {
            case ANNOUNCEMENTS_TYPE.UPLOAD:
              text = this.translate('uploadAnnouncement', { fileNames: text });
              break;
            case ANNOUNCEMENTS_TYPE.DELETE:
              text = this.translate('deleteAnnouncement', { fileNames: text });
              break;
            default:
              text = '';
              break;
          }
          alertDiv.textContent = text;
        }
      }
      setTimeout(this.removeAnnouncement, REMOVE_ANNOUNCEMENT_TIMEOUT);
    }
  };

  /**
   * Remove announcement from DOM.
   */
  removeAnnouncement = (): void => {
    let el: HTMLElement = this.isModuleMounted && this.getDomNode();
    if (el) {
      let alertDiv: HTMLDivElement | null = el.querySelector(`.${ACCESSIBILITY_ANNOUNCEMENTS_CLASS}`);
      if (alertDiv) {
        alertDiv.textContent = '';
      }
    }
  };

  /**
   * Sets new data and droppedFiles and updates total count.
   * @param data
   * @param droppedFiles
   */
  updateStateAfterDeletingFiles = (data: Partial<DATA_ITEM>[], droppedFiles: File[]): void => {
    this.setState({
      data,
      droppedFiles,
      total: data.length
    }, () => {
      const { analyzingAfterUploadConfig = {} } = this.props;
      const { onChangeFileStatuses } = analyzingAfterUploadConfig;
      if (!_.isEmpty(analyzingAfterUploadConfig) && onChangeFileStatuses) {
        onChangeFileStatuses([...data]);
      }
    });
  };

  deleteFile = (e: React.MouseEvent<HTMLButtonElement>, dataItem: Partial<DATA_ITEM>): void => {
    let { data, droppedFiles } = this.state as UPLOAD_PANEL_STATE;
    let index: number = _.findIndex(data, dataItem);
    if (index > -1) {
      let deletedFiles: Partial<DATA_ITEM>[] = data.splice(index, 1);
      droppedFiles.splice(index, 1);
      this.updateStateAfterDeletingFiles(data, droppedFiles);
      this.setAnnouncements(ANNOUNCEMENTS_TYPE.DELETE, deletedFiles);
      if (deletedFiles?.length) {
        Dispatcher.dispatch('cancelSingeUpload', Position.end, {
          cancelRequestForUploadGroup: this.moduleId,
          cancelRequestForFile: deletedFiles[0].fileName
        });
      }
    }
  };

  /**
   * Return loading cell if data is not populated
   * @param cellProps
   */
  loadingCell = (cellProps: GridCellProps): JSX.Element => (
    <span
      className={'k-icon k-i-loading'}
    />);

  /**
   * File name cell render
   */
  fileNameRenderer = (cellProps: GridCellProps): JSX.Element => {
    const { dataItem, field } = cellProps;
    const renderFileNameAsLink: boolean = _.isFunction(this.props.onFileNameClick);
    let value;
    if (field) {
      value = dataItem[field];
    }
    let fileNameCell = this.loadingCell(cellProps);
    if (value) {
      fileNameCell = renderFileNameAsLink ? (
        <span
          role={'link'}
          className={'tp-link'}
          onClick={(e: React.MouseEvent) => this.props.onFileNameClick(e, dataItem)}
          tabIndex={0}
        >{value}</span>
      ) : value;
    }
    return fileNameCell;
  };

  /**
   * Grid cell render
   * @param cellProps
   */
  cellRender = (cellProps: GridCellProps): JSX.Element => {
    const { analyzingAfterUploadConfig = {}, errorMessages } = this.props;
    const { dataItem, field } = cellProps;
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let child;
    let additionalTdPropsMain: any = {};

    let value;
    if (field) {
      value = dataItem[field];
    }
    const analyzingAfterUpload = !_.isEmpty(analyzingAfterUploadConfig) && value === UPLOAD_FILE_STATUS.uploaded;
    if (analyzingAfterUpload && field === 'status') {
      const { actionName, params = {} } = analyzingAfterUploadConfig;
      const findItem = this.previousDataItemAnalyzed.find((item: any) => _.isEqual(dataItem, item));
      if (_.isEmpty(findItem)) {
        this.previousDataItemAnalyzed.push(dataItem);
        this.dispatch(actionName, {
          stack: true,
          pos: Position.end
        }, {
          ...dataItem,
          ...params,
          uploadPanelModuleId: this.moduleId
        });
      }
    }
    switch (field) {
      case DELETE_FILE_DATA_INDEX:
        additionalTdPropsMain.className = 'tp-action-cell k-text-center';
        child = (
          <PuzzleButton
            // <debug>
            data-cy={'delete-file-btn'}
            // </debug>
            className={'tp-delete-files'}
            iconClass={'tp-icon tp-x'}
            size={'small'}
            fillMode={'flat'}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.deleteFile(e, dataItem)}
          />
        );
        break;
      case DEFAULT_SELECTION_FIELD:
        additionalTdPropsMain.className = 'tp-select-cell';
        child = this.selectCellRenderer(cellProps);
        break;
      case 'fileName':
        additionalTdPropsMain.className = 'tp-file-name-cell';
        child = this.fileNameRenderer(cellProps);
        break;
      case 'status':
        additionalTdPropsMain.className = 'k-text-center';
        if (value === UPLOAD_FILE_STATUS.analysing && dataItem.fileName) {
          additionalTdPropsMain.className = 'tp-progress-cell';
          child = this.getProgressBar(dataItem);
        } else {
          let CellStatusCls = '';
          if (value !== UPLOAD_FILE_STATUS.analysing) {
            CellStatusCls = 'tp-icon tp-icon-sm tp-';
            CellStatusCls += (
              ([UPLOAD_FILE_STATUS.uploaded, UPLOAD_FILE_STATUS.analyzed].includes(value) && dataItem.displayedStatus !== UPLOAD_FILE_STATUS.failed)
              ||
              value === UPLOAD_FILE_STATUS.success)
              ? 'check-outline tp-color-success' : 'warning-triangle';
          }
          let title = dataItem.displayedStatus === UPLOAD_FILE_STATUS.failed ? this.translate('noTUFoundInFile') : errorMessages?.incorrectFileFormat || this.translate('incorrectFileFormat');
          if (dataItem.displayedStatus === UPLOAD_FILE_STATUS.failed && dataItem.errorMessages?.length) {
            title = '';
            dataItem.errorMessages.forEach((errorItem, errorIndex) => {
              title += errorIndex === 0 ? errorItem.message : `\n${errorItem.message}`;
            });
          }
          child = value === UPLOAD_FILE_STATUS.failed || dataItem.displayedStatus === UPLOAD_FILE_STATUS.failed ? (
            <PuzzleTooltip
              className={'tp-upload-status-tooltip tp-puzzle-tooltip'}
              position={'top'}
              anchorElement={'target'}
            >
              <span
                title={title}
                className={`${CellStatusCls}`}
              />
            </PuzzleTooltip>
          ) : (analyzingAfterUpload ?
            (
              <span
              // <debug>
                data-cy={'cell-status'}
                // </debug>
              >{this.translate('analyzing')}</span>
            )
            : (
              <span
              // <debug>
                data-cy={'cell-status'}
                // </debug>
                className={`${CellStatusCls}`}
              />)
          );
        }
        break;
      default:
        child = <>{value}</>;
        break;
    }
    let { element, tdProps: additionalTdProps = {} } = this.getCustomCellRendererData(cellProps);
    if (element) {
      child = element;
    }
    additionalTdPropsMain = {
      ...additionalTdPropsMain,
      ...additionalTdProps
    };
    return (
      <td
        {...tdProps}
        {...additionalTdPropsMain}
      >{child}
      </td>
    );
  };

  /**
   * Renders footer cells
   * @param {GridFooterCellProps} props
   */
  private footerCellRender = (props: GridFooterCellProps) => {
    const { footerClassName } = this.props;
    const { field } = props;
    const { columns, data } = this.state;
    const column = columns.find(item => item.dataIndex === field);
    let footerCell = null;
    const footerConfig = column.footer;
    if (footerConfig) {
      let { type, colSpan, style, value, className } = footerConfig;
      if (footerClassName) {
        className += ` ${footerClassName}`;
      }
      let fieldValue = value;
      if (type === 'sum') {
        fieldValue = 0;
        data.forEach((item) => {
          const val = item[field];
          if (_.isNumber(val)) {
            fieldValue += val;
          }
        });
        footerCell = (
          <td
            data-cy={'footer-sum-value'}
            colSpan={colSpan}
            style={style}
            className={className}
          >
            <b>{fieldValue}</b>
          </td>
        );
      } else {
        footerCell = (
          <td colSpan={colSpan} style={style} className={className}>
            {fieldValue}
          </td>
        );
      }
    }
    return footerCell;
  }

  /**
   * Header selection handler.
   * @param event
   */
  headerSelectionChange = (event: GridHeaderSelectionChangeEvent | CheckboxChangeEvent): void => {
    const checked = !!event.nativeEvent?.target?.checked;
    const { data } = this.state as UPLOAD_PANEL_STATE;
    const { pageSize } = this.props as UPLOAD_PANEL_PROPS;
    let newData;
    let selectAllEnabled = data.length > 0 && !_.isNumber(data[0].Index);
    if (selectAllEnabled) {
      newData = data.map((item: Partial<DATA_ITEM>) => {
        item.selected = checked;
        return item;
      });
    } else {
      newData = data.map((item: Partial<DATA_ITEM>) => {
        if (_.isNumber(item.Index) && (item.Index < pageSize)) {
          item.selected = checked;
        }
        return item;
      });
    }
    this.setState({
      data: newData,
      headerSelection: checked
    });
  };

  /**
   * Custom selected column header cell render.
   */
  selectedHeaderColumnCellRender = (): JSX.Element => {
    const { headerSelection } = this.state as UPLOAD_PANEL_STATE;
    return (
      <PuzzleCheckbox
        // <debug>
        data-cy={'select-all-header-checkbox'}
        // </debug>
        checked={headerSelection}
        className={'tp-files-grid-header-select-cell'}
        onChange={this.headerSelectionChange}
        aria-label={this.translate('checkboxHeaderLabel')}
      />
    );
  };

  /**
   * Method that renders files grid columns.
   * @param column
   */
  renderColumn = (column: Partial<COLUMN_DATA>): JSX.Element => {
    let dataIndex: string = column.dataIndex;
    const columnAlias = dataIndex.split('.')[0];
    const isSortable: boolean = !!this.sortableColumns.find((column: SORTABLE_COLUMN) => column.field === dataIndex);
    const columnProps: GridColumnProps = {
      field: dataIndex,
      headerCell: dataIndex === DEFAULT_SELECTION_FIELD && this.selectedHeaderColumnCellRender,
      footerCell: this.footerCellRender,
      title: this.translate(`columnHeaders.${columnAlias}`),
      cell: this.cellRender,
      sortable: isSortable,
      minResizableWidth: 100,
      resizable: dataIndex !== DEFAULT_SELECTION_FIELD,
      width: column.width
    };

    if (dataIndex === 'status') {
      Object.assign(columnProps, {
        className: 'k-text-center'
      });
    }

    let { headerProps = {} } = this.getCustomCellRendererData({
      field: dataIndex,
      dataItem: {}
    } as any);

    return (
      <GridColumn 
        {...columnProps} 
        {...headerProps} 
        key={dataIndex}
      />
    );
  };

  /**
   * Drag leave event handler.
   * @param event
   */
  dragOverHandler = (event: React.DragEvent): void => {
    const { dragging } = this.state as UPLOAD_PANEL_STATE;
    if (!dragging) {
      this.setState({
        dragging: true
      });
    }
  };

  /**
   * Render for displaying search results.
   */
  searchResultsRender = (): JSX.Element => {
    const { data } = this.state as UPLOAD_PANEL_STATE;
    let counter: COUNTER = data.reduce((cnt: COUNTER, item: Partial<DATA_ITEM>) => {
      if (typeof item.isValid === 'boolean') {
        if (item.isValid) {
          cnt.valid++;
        } else {
          cnt.invalid++;
        }
      } else {
        if ([UPLOAD_FILE_STATUS.uploaded, UPLOAD_FILE_STATUS.analyzed].includes(item.status)) {
          cnt.valid++;
        } else {
          cnt.invalid++;
        }
      }
      return cnt;
    }, { valid: 0, invalid: 0 });
    return (
      <Toolbar
        className={'tp-search-results k-d-flex k-align-items-center'}
      >
        <ToolbarItem
          className={`tp-valid-files-count ${counter.valid ? '' : 'tp-no-files'}`}
        >
          <>
            <i
              className={'tp-icon tp-icon-sm tp-check-circle'}
            ></i>
            {this.translate('validFiles', {
              numberOfValidFiles: counter.valid
            })}
          </>
        </ToolbarItem>
        <span className={'tp-separator'}>|</span>
        <ToolbarItem
          className={`tp-invalid-files-count ${counter.invalid ? '' : 'tp-no-files'}`}
        >
          <>
            <i
              className={'tp-icon tp-icon-sm tp-warning-triangle'}
            ></i>
            {
              this.translate('invalidFiles', {
                numberOfInvalidFiles: counter.invalid
              })
            }
          </>
        </ToolbarItem>
      </Toolbar>
    );
  };

  /**
   * Renders file information. While files are uploading there will be displayed analysing message, and after files
   * are uploaded, there will be statics of successfully uploaded and failed files.
   */
  renderFileIndicator = (): JSX.Element => {
    const { moduleIsUploading } = this.state as UPLOAD_PANEL_STATE;
    return (
      <Container
        hBox
        alignItemsCenter
        wrap
        className={'tp-files-counter tp-text-semibold tp-font-size-sm'}
      >
        {moduleIsUploading ? this.analysingRender() : this.searchResultsRender()}
      </Container>
    );
  };

  /**
   * Render checkbox for extract zip option based on passed `extractArchive` configuration.
   */
  renderExtractCheckBox = (): JSX.Element | null => {
    const { extractArchive } = this.state as UPLOAD_PANEL_STATE;
    const { isUploadEnabled, uploadPanelLabels } = this.props as UPLOAD_PANEL_PROPS;
    const { extractArchiveLabel } = uploadPanelLabels;
    return extractArchive.enabled ? (
      <ToolbarItem className={'tp-extract-checkbox-warp'}>
        <PuzzleCheckbox
          label={extractArchiveLabel ?? this.translate('extractArchive')}
          value={extractArchive.value}
          disabled={!isUploadEnabled}
          onChange={(event) => {
            this.setState({
              extractArchive: {
                value: event.value,
                enabled: extractArchive.enabled
              }
            });
          }}
        />
      </ToolbarItem>
    ) : null;
  };

  /**
   * Renderer for upload status section.
   */
  createUploadStatusSection = (): JSX.Element => {
    const { droppedFiles, moduleIsUploading } = this.state as UPLOAD_PANEL_STATE;
    const { showDeleteAllFilesButton = true } = this.props as UPLOAD_PANEL_PROPS;
    let sourceFilesGridFooterCls = classNames(
      'tp-files-uploaded-info-bar',
      {
        'k-display-none': !droppedFiles.length
      }
    );
    return (
      <div className={sourceFilesGridFooterCls}>
        {!!droppedFiles.length ?
          <Toolbar
            className={'tp-info-bar-actions k-hbox k-align-items-center'}
          >
            <ToolbarItem>{this.renderFileIndicator()}</ToolbarItem>
            {this.renderExtractCheckBox()}
            {showDeleteAllFilesButton &&
              <ToolbarItem>
                <KendoButton
                  // <debug>
                  data-cy={'delete-all-btn'}
                  // </debug>
                  disabled={moduleIsUploading}
                  iconClass={'tp-icon tp-delete'}
                  size={'small'}
                  themeColor={'primary'}
                  fillMode={'flat'}
                  onClick={this.deleteMultipleFiles}
                >
                  {this.translate('deleteFiles')}
                </KendoButton>
              </ToolbarItem>
            }
            <ToolbarItem>
              <KendoButton
                // <debug>
                data-cy={'add-more-files-btn'}
                // </debug>
                disabled={moduleIsUploading}
                iconClass={'tp-icon tp-add'}
                themeColor={'primary'}
                fillMode={'flat'}
                size={'small'}
                onClick={this.fileUploadHandler}
              >
                {this.translate('addMoreFiles')}
              </KendoButton>
            </ToolbarItem>
          </Toolbar>
          : null
        }
      </div>
    );
  };

  /**
   * Triggers click event on file input field.
   */
  fileUploadHandler = (): void => {
    this.dragAndDropReference.fileUploadHandler();
  };

  /**
   * Removes selected files if there is selection column. Otherwise deletes all files.
   */
  deleteMultipleFiles = (): void => {
    const { selectionField, prependSelectionColumn } = this.props as UPLOAD_PANEL_PROPS;
    let { data } = this.state as UPLOAD_PANEL_STATE;
    let filesToRemove: Partial<DATA_ITEM>[];
    if (prependSelectionColumn) {
      // if there is checkbox column then delete all selected, otherwise delete all files
      filesToRemove = data.filter((item: Partial<DATA_ITEM>) => item[selectionField]);
    } else {
      filesToRemove = data;
    }
    if (filesToRemove.length) {
      this.removeFile(filesToRemove);
    }
  };

  /**
   * Remove file.
   */
  removeFile = (files: Partial<DATA_ITEM>[]): void => {
    let { data = [], droppedFiles: originalDroppedFiles } = this.state as UPLOAD_PANEL_STATE;
    data = _.cloneDeep(data);
    let droppedFiles: File[] = [...originalDroppedFiles];
    let length: number = droppedFiles.length;
    if (files.length > 0 && _.isFunction(this.props.onBeforeDeleteFiles)) {
      files = this.props.onBeforeDeleteFiles(files);
      if (!Array.isArray(files)) {
        // <debug>
        error('onBeforeDeleteFiles must return files!');
        files = [];
        // </debug>
      }
    }
    let filesLength: number = files.length;
    let counter: number;
    let deletedFiles: any[] = [];
    while (length--) {
      counter = 0;
      while (filesLength > counter) {
        if (droppedFiles[length].name === files[counter].fileName) {
          // deletedFiles.push(...data.splice(length, 1));
          droppedFiles.splice(length, 1);
          break;
        }
        counter++;
      }
    }
    files.forEach((item) => {
      let index: number = _.findIndex(data, {
        fileName: item?.fileName
      });
      if (index > -1) {
        deletedFiles.push({ ...data[index] });
        data.splice(index, 1);
      }
    });
    this.updateStateAfterDeletingFiles(data, droppedFiles);
    this.setState({
      headerSelection: false
    }, () => {
      this.setAnnouncements(ANNOUNCEMENTS_TYPE.DELETE, deletedFiles);
    });
  };

  /**
   * Selection change handler.
   */
  onSelectionChange = (event: GridSelectionChangeEvent): void => {
    let { dataItem, selectedField } = event;
    let { data } = this.state as UPLOAD_PANEL_STATE;
    let arrayIndex = data.findIndex((item: Partial<DATA_ITEM>) => item === dataItem);
    if (arrayIndex > -1) {
      let item = data[arrayIndex];
      item.selected = !dataItem[selectedField];
      const headerSelection: boolean = data.every(item => item.selected);
      this.setState({
        data,
        headerSelection
      });
    }
  };

  /**
   * Page event handler, sets skip.
   */
  onPageChange = (event: GridPageChangeEvent): void => {
    const skip: number = event.page.skip;
    this.setState({
      skip
    });
  };

  /**
   * Renders grid with files.
   */
  createUploadedFilesList = (): JSX.Element => {
    const {
      data,
      sort,
      dragging
    } = this.state as UPLOAD_PANEL_STATE;
    const {
      rowHeight,
      selectionField,
      sortable
    } = this.props as UPLOAD_PANEL_PROPS;
    const gridCls = classNames(
      'tp-upload-grid', {
        'tp-empty-grid': !data.length
      },
      'tp-uploaded-files-grid', {
        'tp-masked': dragging,
        'tp-scroll': this.state.droppedFiles.length > 1
      }
    );

    let gridProps: GRID_PROPS_TYPES = {
      navigatable: true,
      className: gridCls,
      data: [...data],
      onHeaderSelectionChange: this.headerSelectionChange,
      onSelectionChange: this.onSelectionChange,
      rowHeight: rowHeight,
      selectedField: selectionField,
      resizable: true
    };
    if (sortable) {
      gridProps = {
        ...gridProps,
        data: orderBy(data, sort),
        sort: sort,
        onSortChange: (e: GridSortChangeEvent): void => {
          this.setState({ sort: e.sort });
        },
        sortable: sortable
      };
    }

    return (
      <Grid
        {...gridProps}
      >
        <GridNoRecords>
          <Container
            className='k-vbox k-justify-content-center k-align-items-center'
          >
            <div className={'tp-grid-empty-state-img files-grid'} />
            <div
              className='tp-grid-empty-state-text k-text-center tp-font-size-xl'
            >
              {this.translate('gridEmptyLabel')}
            </div>
          </Container>
        </GridNoRecords>
        {this.state.columns.filter((this.isHidden)).map(this.renderColumn)}
      </Grid>
    );
  };

  /**
   * Disables dragging.
   */
  disableDragging = (): void => {
    if (this.state.dragging) {
      this.setState({ dragging: false });
    }
  };

  /**
   *  Sets uploaded files to state and upades total.
   * @param droppedFiles
   */
  setDroppedFiles = (droppedFiles: File[]): void => {
    let { droppedFiles: previouslyDroppedFiles } = this.state as UPLOAD_PANEL_STATE;
    previouslyDroppedFiles = [...previouslyDroppedFiles, ...droppedFiles];
    this.setState({
      droppedFiles: previouslyDroppedFiles,
      total: previouslyDroppedFiles.length
    });
  };

  /**
   * Gets instructions based on instruction object.
   * @param instruction
   */
  getInstructions = (instruction: {
    title: string,
    message: string
  }): JSX.Element | null => _.isObject(instruction) ?
    <Container className="tp-drag-and-drop-formats-supported tp-font-size">
      <h2
        className='tp-font-size tp-text-semibold'
        // <debug>
        data-cy={'instructions-title'}
        // </debug>
      >{instruction.title}</h2>
      <PuzzleInfoBar>
        <HBox gap={2} alignItemsStart>
          <i
            aria-hidden={'true'}
            className={'tp-icon tp-icon-sm tp-info'}
          />
          <span>{instruction.message}</span>
        </HBox>
      </PuzzleInfoBar>
    </Container>
    : null;

  /**
   * @inheritDoc
   */
  renderBody() {
    const {
      data,
      extractArchive,
      dragging
    } = this.state as UPLOAD_PANEL_STATE;
    const {
      isUploadEnabled,
      id,
      showUploadStatus,
      uploadConfig,
      concurrently = false,
      instruction,
      fileExtension,
      dataCy,
      multipleUpload,
      dragAndDropLabels
    } = this.props as UPLOAD_PANEL_PROPS;

    return (
      <Container
        id={id}
        dataCy={dataCy}
        className={`tp-drag-and-drop-wrapper ${_.isObject(instruction) ? 'tp-with-instructions' : ''}`}
        flex1
      >
        {this.getInstructions(instruction)}
        <div onDragEnter={this.dragOverHandler}>
          <div
            role={'alert'}
            className={`sr-only ${ACCESSIBILITY_ANNOUNCEMENTS_CLASS}`}
          />
          <DragAndDropArea
            allowUpload={isUploadEnabled}
            fileExtension={fileExtension}
            multipleUpload={multipleUpload}
            uploadConfig={uploadConfig}
            renderExtractCheckBox={this.renderExtractCheckBox.bind(this)}
            ref={ref => this.dragAndDropReference = ref}
            concurrently={concurrently}
            dragging={dragging}
            uploadPanelModuleId={this.moduleId}
            extractArchive={extractArchive}
            onDragLeave={this.onDragLeave}
            onDropHandler={this.onDragHandler}
            uploadedFilesLength={data.length}
            dragAndDropLabels={dragAndDropLabels}
          />
          {data.length ? this.createUploadedFilesList() : null}
          {showUploadStatus && this.createUploadStatusSection()}
        </div>
      </Container>
    );
  }

  /**
   * Gets sort direction.
   * @param ascending
   */
  getSortDirection = (ascending: boolean): 'asc' | 'desc' => ascending ? 'asc' : 'desc';

  /**
   * Gets progress bar for passed dataItem.
   * @param dataItem
   */
  private getProgressBar = (dataItem: Partial<DATA_ITEM>): JSX.Element => {
    const { progresses } = this.state;
    const { showedUploadProgress } = this.props;
    let uploadGroupId = this.moduleId;
    return (
      <div className={'tp-progress-cell-inner-wrap'}>
        {showedUploadProgress ? <ProgressBar
          key={`ProgressBar_${uploadGroupId}_${dataItem.fileName}`}
          className={'tp-uploaded-list-item-status k-text-ellipsis'}
          keepPercentagesWhenUploaded={true}
          file={dataItem}
          fileName={dataItem.fileName}
          group={uploadGroupId}
          progresses={progresses}
        /> : <div>{this.translate('uploading')}</div>}
      </div> 
    );
  };

  /**
   * Get custom renderer form `cellRenderers` if exists.
   * @param cellProps
   */
  private getCustomCellRendererData = (cellProps: GridCellProps): CUSTOM_RENDERER_RETURN_TYPE => {
    const { field } = cellProps;
    const { cellRenderers = {} } = this.props as UPLOAD_PANEL_PROPS;
    let value: CUSTOM_RENDERER_RETURN_TYPE = {
      element: null
    };
    if (_.isFunction(cellRenderers[field])) {
      value = cellRenderers[field](cellProps);
    }
    return value;
  };

  /**
   * Checks if one or more of uploaded files is folder.
   * Current implementation is compatible with chrome and firefox.
   * @param items
   */
  private isDirectoryUploaded = (items: DataTransferItemList): boolean => Array.from(items || []).some((fileObject: DataTransferItem) => fileObject.webkitGetAsEntry().isDirectory);

  /**
   * Handler for `dragleave` event.
   * @param event
   */
  private onDragLeave = (event: React.DragEvent): void => {
    this.disableDragging();
  };

  /**
   * Handler for `drag` event. Returns `true` if folder is not uploaded. If folder is uploading then warning will
   * appear and upload will be prevented.
   * @param e
   */
  private onDragHandler = (e: React.DragEvent): boolean => {
    this.disableDragging();
    const { files, items } = e.dataTransfer;
    const directoryExists = this.isDirectoryUploaded(items);
    let stateData: Partial<UPLOAD_PANEL_STATE> = {
      moduleIsUploading: false
    };
    if (directoryExists) {
      this.disableDragging();
      this.dispatch('invalidFileDropWarning');
    } else {
      stateData = {
        moduleIsUploading: true
      };
    }
    let precedeWithUpload: boolean = !directoryExists;
    if (precedeWithUpload) {
      this.setState(stateData);
    }
    return precedeWithUpload;
  };

  /**
   * Appends additional column.
   * @param config
   */
  private addAdditionalColumns(config: Partial<COLUMN_DATA>[]): Partial<COLUMN_DATA>[] {
    const { appendCloseColumn, prependSelectionColumn } = this.props as UPLOAD_PANEL_PROPS;
    if (appendCloseColumn && !_.find(config, {
      dataIndex: DELETE_ROW_ADDITIONAL_COLUMN.dataIndex
    })) {
      if (this.props.footerClassName) {
        DELETE_ROW_ADDITIONAL_COLUMN.footer = {
          value: ''
        };
      }
      config.push(DELETE_ROW_ADDITIONAL_COLUMN);
    }
    if (prependSelectionColumn && !_.find(config, {
      dataIndex: SELECTION_COLUMN.dataIndex
    })) {
      if (this.props.footerClassName) {
        SELECTION_COLUMN.footer = {
          value: ''
        };
      }
      config = [SELECTION_COLUMN, ...config];
    }
    return config;
  }

  private analysingRender() {
    return (
      <>
        <div className={'tp-icon tp-refreshNew tp-color-accent'} />
        <span
          className={'tp-files-uploaded-info'}
        >{this.translate('analysing')}</span>
      </>
    );
  }
}

export default UploadPanel;
